/**
 * src/components/spatial/SpatialObjectFloorLayer.tsx
 *
 * Per-floor fill-extrusion layer. Renders one extruded slab per
 * spatial object whose `objectType === 'floor'`, optionally
 * constrained by the user's selected floorNumber or "explode" mode.
 *
 * In explode mode, the paint expressions lift each floor by
 * `(floorNumber - 1) × explodeGapMeters` so the user can see between
 * floors. Slice mode hides this layer entirely (the slice plate is
 * the dominant surface in that mode).
 */

import React, { useEffect } from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';
import { useSpatialObjects } from './SpatialObjectsContext';
import {
  SPATIAL_LAYER_IDS,
  SPATIAL_SOURCE_ID,
  buildExtrusionBaseExpr,
  buildExtrusionColorExpr,
  buildExtrusionHeightExpr,
  buildExtrusionOpacityExpr,
  buildKindFilter,
} from './spatialLayerUtils';
import { useStrictModeSafeLayer } from './useStrictModeSafeLayer';

export interface SpatialObjectFloorLayerProps {
  provider: SpatialMapProvider;
  sourceId?: string;
  layerId?: string;
  beforeId?: string;
}

const SpatialObjectFloorLayer: React.FC<SpatialObjectFloorLayerProps> = ({
  provider,
  sourceId = SPATIAL_SOURCE_ID,
  layerId  = SPATIAL_LAYER_IDS.floor,
  beforeId,
}) => {
  const { view, filters } = useSpatialObjects();

  useStrictModeSafeLayer({
    provider,
    layerId,
    beforeId,
    buildSpec: () => ({
      id:     layerId,
      type:   'fill-extrusion',
      source: sourceId,
      filter: buildKindFilter('floor', view, filters) as unknown[],
      paint:  {
        'fill-extrusion-base':    buildExtrusionBaseExpr(view, 'floor'),
        'fill-extrusion-height':  buildExtrusionHeightExpr(view, 'floor'),
        'fill-extrusion-color':   buildExtrusionColorExpr('floor'),
        'fill-extrusion-opacity': buildExtrusionOpacityExpr('floor'),
      },
    }),
  });

  // Re-bind paint + filter when state changes. Filter expressions are
  // small so re-applying them every render is essentially free.
  useEffect(() => {
    if (!provider.hasLayer(layerId)) return;
    provider.setFilter(layerId, buildKindFilter('floor', view, filters) as unknown[]);
    provider.setPaintProperty(layerId, 'fill-extrusion-base',   buildExtrusionBaseExpr(view, 'floor'));
    provider.setPaintProperty(layerId, 'fill-extrusion-height', buildExtrusionHeightExpr(view, 'floor'));
  }, [provider, layerId, view, filters]);

  return null;
};

export default SpatialObjectFloorLayer;
